import { logger } from '@equip/utils/logger';

/**
 * Authentication event logger for tracking auth flows and metrics.
 *
 * IMPORTANT: This is structured logging only, not APM telemetry (Code Review Issue #CR2)
 * Does not provide: metrics dashboards, alerting, distributed tracing, or performance monitoring.
 * For production monitoring, integrate: Application Insights, DataDog, or New Relic.
 *
 * Current limitations:
 * - No metric aggregation (counts, rates, percentiles)
 * - No alerting on auth failure spikes
 * - No performance tracking (P50, P99 latencies)
 * - No distributed tracing across services
 */
class AuthLogger {
  /**
   * Track an authentication event with optional metadata
   */
  track(event: string, metadata?: Record<string, unknown>): void {
    logger.info(`[AuthMetrics] ${event}`, {
      component: 'AuthLogger',
      event,
      timestamp: new Date().toISOString(),
      ...metadata,
    });
  }

  /**
   * Track an authentication error
   */
  trackError(event: string, error: unknown, metadata?: Record<string, unknown>): void {
    logger.error(`[AuthMetrics] ${event}`, {
      component: 'AuthLogger',
      event,
      error: String(error),
      timestamp: new Date().toISOString(),
      ...metadata,
    });
  }

  /**
   * Track auth flow duration
   */
  trackDuration(event: string, startTime: number, metadata?: Record<string, unknown>): void {
    const duration = Date.now() - startTime;
    logger.info(`[AuthMetrics] ${event}`, {
      component: 'AuthLogger',
      event,
      duration,
      timestamp: new Date().toISOString(),
      ...metadata,
    });
  }
}

// Export as 'telemetry' for backward compatibility
export const telemetry = new AuthLogger();
